import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { ApiError, ApiResponse } from "@/api"
import {
   AlarmScheduleCreateRequest,
   AlarmScheduleResponse,
   AlarmScheduleUpdateRequest,
   requestAlarmSchedules,
   requestCreateAlarmSchedule,
   requestDeleteAlarmSchedule,
   requestLevelAlarmSchedules,
   requestUpdateAlarmSchedule,
} from "@/api/AlarmSchedulesService"
import { HttpCodes } from "@/constants/globals"

interface AlarmSchedulesState {
   alarmSchedules: AlarmScheduleResponse[]
   loading: boolean
   error: number | null
   loadingAlarmSchedule: boolean
   errorAlarmSchedule: number | null
   levelAlarmSchedules: AlarmScheduleResponse[]
   loadingLevelAlarmSchedules: boolean
   errorLevelAlarmSchedules: number | null
   levelAlarmSchedulesRequestId: string | null
}

export const initialState: AlarmSchedulesState = {
   alarmSchedules: [],
   loading: false,
   error: null,
   loadingAlarmSchedule: false,
   errorAlarmSchedule: null,
   levelAlarmSchedules: [],
   loadingLevelAlarmSchedules: false,
   errorLevelAlarmSchedules: null,
   levelAlarmSchedulesRequestId: null,
}

export const fetchAlarmSchedules = createAsyncThunk(
   "alarmSchedules/fetchAlarmSchedules",
   async (_, thunkAPI) => {
      try {
         const response: ApiResponse<AlarmScheduleResponse[]> =
            await requestAlarmSchedules()
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const fetchLevelAlarmSchedules = createAsyncThunk(
   "alarmSchedules/fetchLevelAlarmSchedules",
   async (levelId: string, thunkAPI) => {
      try {
         const response: ApiResponse<AlarmScheduleResponse[]> =
            await requestLevelAlarmSchedules(levelId)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const createAlarmSchedule = createAsyncThunk(
   "alarmSchedules/createAlarmSchedule",
   async (alarmSchedule: AlarmScheduleCreateRequest, thunkAPI) => {
      try {
         const response: ApiResponse<AlarmScheduleResponse> =
            await requestCreateAlarmSchedule(alarmSchedule)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const updateAlarmSchedule = createAsyncThunk(
   "alarmSchedules/updateAlarmSchedule",
   async (
      {
         alarmScheduleId,
         alarmSchedule,
      }: {
         alarmScheduleId: string
         alarmSchedule: AlarmScheduleUpdateRequest
      },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<AlarmScheduleResponse> =
            await requestUpdateAlarmSchedule(alarmScheduleId, alarmSchedule)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const deleteAlarmSchedule = createAsyncThunk(
   "alarmSchedules/deleteAlarmSchedule",
   async (alarmScheduleId: string, thunkAPI) => {
      try {
         const response: ApiResponse<null> =
            await requestDeleteAlarmSchedule(alarmScheduleId)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

const alarmSchedulesSlice = createSlice({
   name: "alarmSchedules",
   initialState,
   reducers: {
      resetAlarmSchedules: () => initialState,
   },
   extraReducers: (builder) => {
      builder
         .addCase(fetchAlarmSchedules.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(fetchAlarmSchedules.fulfilled, (state, action) => {
            state.loading = false
            state.alarmSchedules = action.payload.data
         })
         .addCase(fetchAlarmSchedules.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
         .addCase(fetchLevelAlarmSchedules.pending, (state, action) => {
            state.levelAlarmSchedules = []
            state.loadingLevelAlarmSchedules = true
            state.errorLevelAlarmSchedules = null
            state.levelAlarmSchedulesRequestId = action.meta.requestId
         })
         // Only the latest request counts; an earlier cell's late answer is dropped.
         .addCase(fetchLevelAlarmSchedules.fulfilled, (state, action) => {
            if (action.meta.requestId !== state.levelAlarmSchedulesRequestId)
               return
            state.loadingLevelAlarmSchedules = false
            state.levelAlarmSchedules = action.payload.data
         })
         .addCase(fetchLevelAlarmSchedules.rejected, (state, action) => {
            if (action.meta.requestId !== state.levelAlarmSchedulesRequestId)
               return
            state.loadingLevelAlarmSchedules = false
            state.errorLevelAlarmSchedules = action.payload as number
         })
         .addCase(createAlarmSchedule.pending, (state) => {
            state.loadingAlarmSchedule = true
            state.errorAlarmSchedule = null
         })
         .addCase(createAlarmSchedule.fulfilled, (state, action) => {
            state.loadingAlarmSchedule = false
            state.alarmSchedules.push(action.payload.data)
         })
         .addCase(createAlarmSchedule.rejected, (state, action) => {
            state.loadingAlarmSchedule = false
            state.errorAlarmSchedule = action.payload as number
         })
         .addCase(updateAlarmSchedule.pending, (state) => {
            state.loadingAlarmSchedule = true
            state.errorAlarmSchedule = null
         })
         .addCase(updateAlarmSchedule.fulfilled, (state, action) => {
            const updated = action.payload.data
            state.loadingAlarmSchedule = false
            state.alarmSchedules = state.alarmSchedules.map((alarmSchedule) =>
               alarmSchedule.id === updated.id ? updated : alarmSchedule
            )
         })
         .addCase(updateAlarmSchedule.rejected, (state, action) => {
            state.loadingAlarmSchedule = false
            state.errorAlarmSchedule = action.payload as number
         })
         .addCase(deleteAlarmSchedule.pending, (state) => {
            state.loadingAlarmSchedule = true
            state.errorAlarmSchedule = null
         })
         .addCase(deleteAlarmSchedule.fulfilled, (state, action) => {
            state.loadingAlarmSchedule = false
            state.alarmSchedules = state.alarmSchedules.filter(
               (alarmSchedule) => alarmSchedule.id !== action.meta.arg
            )
         })
         .addCase(deleteAlarmSchedule.rejected, (state, action) => {
            state.loadingAlarmSchedule = false
            state.errorAlarmSchedule = action.payload as number
         })
   },
})

export const { resetAlarmSchedules } = alarmSchedulesSlice.actions

export default alarmSchedulesSlice.reducer
